import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Target, 
  Activity, 
  AlertCircle, 
  CheckCircle, 
  Loader2,
  Settings,
  PlayCircle,
  StopCircle,
  Zap,
  Shield,
  Eye,
  Copy,
  ExternalLink,
  Plus,
  Trash2,
  DollarSign,
  Droplets,
  Menu,
  X
} from 'lucide-react';
import { 
  Connection, 
  PublicKey, 
  Transaction, 
  LAMPORTS_PER_SOL, 
  VersionedTransaction,
  sendAndConfirmTransaction,
  TransactionMessage,
  ComputeBudgetProgram
} from '@solana/web3.js';
import { 
  TOKEN_PROGRAM_ID, 
  TOKEN_2022_PROGRAM_ID, 
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  ASSOCIATED_TOKEN_PROGRAM_ID
} from '@solana/spl-token';
import { useWallet } from '../contexts/WalletContext';
import axios from 'axios';

// Configuration
const HELIUS_RPC = 'https://mainnet.helius-rpc.com/?api-key=37327d2d-9742-4fc4-b0d0-57d489b78697';
const HELIUS_WSS = 'wss://mainnet.helius-rpc.com/?api-key=37327d2d-9742-4fc4-b0d0-57d489b78697';
const JUPITER_API = 'https://quote-api.jup.ag/v6';

// Raydium AMM Program IDs
const RAYDIUM_AMM_V4 = new PublicKey('675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8');
const RAYDIUM_AUTHORITY_V4 = new PublicKey('5Q544fKrFoe6tsEbD7S8EmxGTJYAKtTVhAW5Q5pge4j1');
const OPENBOOK_PROGRAM = new PublicKey('srmqPvymJeFKQ4zGQed1GFppgkRHL9kaELCbyksJtPX');

interface SniperTarget {
  id: string;
  tokenAddress: string;
  name?: string;
  symbol?: string;
  decimals?: number;
  status: 'waiting' | 'monitoring' | 'buying' | 'bought' | 'failed' | 'stopped';
  hasLiquidity: boolean;
  liquiditySOL: number;
  liquidityToken: number;
  pricePerToken: number;
  poolAddress?: string;
  marketId?: string;
  buyAmount: number;
  maxSlippage: number;
  priorityFee: number;
  txSignature?: string;
  error?: string;
  addedAt: Date;
  boughtAt?: Date;
  retryCount: number;
  maxRetries: number;
  liquidityCheckCount: number;
  lastChecked?: Date;
}

interface SniperSettings {
  autoSnipe: boolean;
  checkInterval: number;
  minLiquiditySOL: number;
  maxLiquiditySOL: number;
  defaultBuyAmount: number;
  defaultSlippage: number;
  defaultPriorityFee: number;
  maxRetries: number;
  useJitoBundle: boolean;
  jitoTip: number;
  multipleTargets: boolean;
  stopAfterSuccess: boolean;
  soundAlert: boolean;
  maxGasFee: number;
}

interface RaydiumPoolData {
  id: string;
  baseMint: string;
  quoteMint: string;
  lpMint: string;
  baseDecimals: number;
  quoteDecimals: number;
  lpDecimals: number;
  version: number;
  programId: string;
  authority: string;
  openOrders: string;
  targetOrders: string;
  baseVault: string;
  quoteVault: string;
  marketVersion: number;
  marketProgramId: string;
  marketId: string;
  marketAuthority: string;
  marketBaseVault: string;
  marketQuoteVault: string;
  marketBids: string;
  marketAsks: string;
  marketEventQueue: string;
  baseReserve: string;
  quoteReserve: string;
}

const ProductionTokenSniper: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const connection = useRef(new Connection(HELIUS_RPC, 'confirmed')).current;
  const ws = useRef<WebSocket | null>(null);
  const monitoringInterval = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // State
  const [targets, setTargets] = useState<SniperTarget[]>([]);
  const [newTokenAddress, setNewTokenAddress] = useState('');
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedTarget, setExpandedTarget] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalMonitored: 0,
    successfulSnipes: 0,
    failedSnipes: 0,
    totalSpent: 0,
    totalProfit: 0
  });

  const [settings, setSettings] = useState<SniperSettings>({
    autoSnipe: true,
    checkInterval: 3,
    minLiquiditySOL: 5,
    maxLiquiditySOL: 500,
    defaultBuyAmount: 0.5,
    defaultSlippage: 30,
    defaultPriorityFee: 0.001,
    maxRetries: 3,
    useJitoBundle: false,
    jitoTip: 0.001,
    multipleTargets: true,
    stopAfterSuccess: false,
    soundAlert: true,
    maxGasFee: 0.01
  });

  // WebSocket connection for real-time monitoring
  useEffect(() => {
    if (isMonitoring && targets.length > 0) {
      connectWebSocket();
      startMonitoring();
    } else {
      disconnectWebSocket();
      stopMonitoring();
    }

    return () => {
      disconnectWebSocket();
      stopMonitoring();
    };
  }, [isMonitoring, targets.length]);

  const connectWebSocket = () => {
    try {
      ws.current = new WebSocket(HELIUS_WSS);

      ws.current.onopen = () => {
        console.log('WebSocket connected to Helius');
        targets.forEach(target => {
          subscribeToToken(target.tokenAddress);
        });
      };

      ws.current.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        if (data.method === 'accountNotification') {
          await handleAccountUpdate(data.params);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.current.onclose = () => {
        console.log('WebSocket disconnected');
        if (isMonitoring) {
          setTimeout(connectWebSocket, 5000);
        }
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const disconnectWebSocket = () => {
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  };

  const subscribeToToken = (tokenAddress: string) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;

    const subscribeMessage = {
      jsonrpc: '2.0',
      id: Date.now(),
      method: 'accountSubscribe',
      params: [
        tokenAddress,
        {
          encoding: 'jsonParsed',
          commitment: 'confirmed'
        }
      ]
    };

    ws.current.send(JSON.stringify(subscribeMessage));
  };

  const handleAccountUpdate = async (params: any) => {
    const { subscription, result } = params;
    console.log('Account update received:', subscription);
    
    for (const target of targets) {
      if (target.status === 'monitoring' || target.status === 'waiting') {
        await checkLiquidity(target.id);
      }
    }
  };

  const startMonitoring = () => {
    if (monitoringInterval.current) {
      clearInterval(monitoringInterval.current);
    }

    checkAllTargets();

    monitoringInterval.current = setInterval(() => {
      checkAllTargets();
    }, settings.checkInterval * 1000);
  };

  const stopMonitoring = () => {
    if (monitoringInterval.current) {
      clearInterval(monitoringInterval.current);
      monitoringInterval.current = null;
    }
  };

  const checkAllTargets = async () => {
    const activeTargets = targets.filter(t => 
      t.status === 'waiting' || t.status === 'monitoring' || 
      (t.status === 'failed' && t.retryCount < t.maxRetries)
    );

    for (const target of activeTargets) {
      await checkLiquidity(target.id);
    }
  };

  const checkLiquidity = async (targetId: string) => {
    const target = targets.find(t => t.id === targetId);
    if (!target) return;

    try {
      updateTarget(targetId, { 
        status: 'monitoring', 
        lastChecked: new Date(),
        liquidityCheckCount: target.liquidityCheckCount + 1
      });

      const liquidityData = await fetchRaydiumLiquidity(target.tokenAddress);
      
      if (liquidityData.hasLiquidity && liquidityData.liquiditySOL >= settings.minLiquiditySOL) {
        console.log(`🎯 Liquidity detected for ${target.symbol || target.tokenAddress}!`);
        
        updateTarget(targetId, {
          hasLiquidity: true,
          liquiditySOL: liquidityData.liquiditySOL,
          liquidityToken: liquidityData.liquidityToken,
          pricePerToken: liquidityData.pricePerToken,
          poolAddress: liquidityData.poolAddress,
          marketId: liquidityData.marketId
        });

        if (settings.autoSnipe && liquidityData.liquiditySOL <= settings.maxLiquiditySOL) {
          await executeBuy(targetId);
        } else {
          if (settings.soundAlert && audioRef.current) {
            audioRef.current.play().catch(e => console.log('Audio play failed:', e));
          }
          updateTarget(targetId, { status: 'waiting' });
        }
      } else {
        updateTarget(targetId, {
          hasLiquidity: false,
          liquiditySOL: liquidityData.liquiditySOL || 0,
          liquidityToken: liquidityData.liquidityToken || 0,
          status: 'monitoring'
        });
      }
    } catch (error: any) {
      console.error(`Error checking liquidity for ${target.tokenAddress}:`, error);
      updateTarget(targetId, { 
        error: error.message,
        status: 'monitoring'
      });
    }
  };

  const fetchRaydiumLiquidity = async (tokenAddress: string): Promise<{
    hasLiquidity: boolean;
    liquiditySOL: number;
    liquidityToken: number;
    pricePerToken: number;
    poolAddress?: string;
    marketId?: string;
  }> => {
    try {
      const response = await axios.get(`https://api.raydium.io/v2/sdk/liquidity/mainnet.json`);
      const pools = response.data.official || [];
      const unOfficial = response.data.unOfficial || [];
      const allPools = [...pools, ...unOfficial];
      
      const tokenPools = allPools.filter((pool: RaydiumPoolData) => 
        pool.baseMint === tokenAddress || pool.quoteMint === tokenAddress
      );

      if (tokenPools.length === 0) {
        return await checkOnChainLiquidity(tokenAddress);
      }

      let maxLiquidity = 0;
      let bestPool = null;
      let pricePerToken = 0;

      for (const pool of tokenPools) {
        try {
          const poolPubkey = new PublicKey(pool.id);
          const poolAccount = await connection.getAccountInfo(poolPubkey);
          
          if (!poolAccount) continue;

          const baseVault = new PublicKey(pool.baseVault);
          const quoteVault = new PublicKey(pool.quoteVault);
          
          const [baseBalance, quoteBalance] = await Promise.all([
            connection.getTokenAccountBalance(baseVault),
            connection.getTokenAccountBalance(quoteVault)
          ]);

          const baseAmount = Number(baseBalance.value.amount) / Math.pow(10, pool.baseDecimals);
          const quoteAmount = Number(quoteBalance.value.amount) / Math.pow(10, pool.quoteDecimals);

          const isBaseToken = pool.baseMint === tokenAddress;
          const solAmount = isBaseToken ? quoteAmount : baseAmount;
          const tokenAmount = isBaseToken ? baseAmount : quoteAmount;

          if (solAmount > maxLiquidity) {
            maxLiquidity = solAmount;
            bestPool = pool;
            pricePerToken = tokenAmount > 0 ? solAmount / tokenAmount : 0;
          }
        } catch (err) {
          console.error(`Error checking pool ${pool.id}:`, err);
        }
      }

      if (bestPool && maxLiquidity > 0) {
        return {
          hasLiquidity: true,
          liquiditySOL: maxLiquidity,
          liquidityToken: maxLiquidity / pricePerToken,
          pricePerToken: pricePerToken,
          poolAddress: bestPool.id,
          marketId: bestPool.marketId
        };
      }

      return {
        hasLiquidity: false,
        liquiditySOL: 0,
        liquidityToken: 0,
        pricePerToken: 0
      };
    } catch (error) {
      console.error('Error fetching Raydium liquidity:', error);
      return {
        hasLiquidity: false,
        liquiditySOL: 0,
        liquidityToken: 0,
        pricePerToken: 0
      };
    }
  };

  const checkOnChainLiquidity = async (tokenAddress: string): Promise<{
    hasLiquidity: boolean;
    liquiditySOL: number;
    liquidityToken: number;
    pricePerToken: number;
    poolAddress?: string;
    marketId?: string;
  }> => {
    try {
      const jupiterResponse = await axios.get(
        `${JUPITER_API}/quote?inputMint=So11111111111111111111111111111111111111112&outputMint=${tokenAddress}&amount=1000000000&slippageBps=${settings.defaultSlippage * 100}`
      );

      if (jupiterResponse.data && jupiterResponse.data.routePlan) {
        const quote = jupiterResponse.data;
        const outputAmount = Number(quote.outAmount) / 1e9;
        const pricePerToken = 1 / outputAmount;

        return {
          hasLiquidity: true,
          liquiditySOL: 10,
          liquidityToken: 10 / pricePerToken,
          pricePerToken: pricePerToken,
          poolAddress: 'jupiter-aggregated',
          marketId: 'jupiter'
        };
      }
    } catch (error) {
      console.log('No Jupiter liquidity found');
    }

    return {
      hasLiquidity: false,
      liquiditySOL: 0,
      liquidityToken: 0,
      pricePerToken: 0
    };
  };

  const executeBuy = async (targetId: string) => {
    if (!publicKey || !isConnected) {
      console.error('Wallet not connected');
      return;
    }

    const target = targets.find(t => t.id === targetId);
    if (!target) return;

    try {
      updateTarget(targetId, { status: 'buying' });
      console.log(`💰 Executing buy for ${target.symbol || target.tokenAddress}...`);

      const jupiterSwap = await executeJupiterSwap(
        target.tokenAddress,
        target.buyAmount,
        target.maxSlippage,
        target.priorityFee
      );

      if (jupiterSwap.success && jupiterSwap.txSignature) {
        updateTarget(targetId, {
          status: 'bought',
          txSignature: jupiterSwap.txSignature,
          boughtAt: new Date()
        });

        setStats(prev => ({
          ...prev,
          successfulSnipes: prev.successfulSnipes + 1,
          totalSpent: prev.totalSpent + target.buyAmount
        }));

        console.log(`✅ Successfully bought ${target.symbol || target.tokenAddress}!`);

        if (settings.soundAlert && audioRef.current) {
          audioRef.current.play().catch(e => console.log('Audio play failed:', e));
        }

        if (settings.stopAfterSuccess) {
          setIsMonitoring(false);
        }
      } else {
        throw new Error(jupiterSwap.error || 'Transaction failed');
      }
    } catch (error: any) {
      console.error(`Failed to buy ${target.tokenAddress}:`, error);
      
      const newRetryCount = target.retryCount + 1;
      
      updateTarget(targetId, {
        status: newRetryCount >= target.maxRetries ? 'failed' : 'monitoring',
        error: error.message,
        retryCount: newRetryCount
      });

      if (newRetryCount >= target.maxRetries) {
        setStats(prev => ({
          ...prev,
          failedSnipes: prev.failedSnipes + 1
        }));
      }
    }
  };

  const executeJupiterSwap = async (
    outputMint: string, 
    amountInSOL: number,
    slippageBps: number,
    priorityFeeLamports: number
  ): Promise<{ success: boolean; txSignature?: string; error?: string }> => {
    try {
      if (!publicKey || !isConnected) {
        throw new Error('Wallet not connected');
      }

      const provider = (window as any).solana;
      if (!provider?.isPhantom) {
        throw new Error('Phantom wallet not found');
      }

      const quoteResponse = await axios.get(`${JUPITER_API}/quote`, {
        params: {
          inputMint: 'So11111111111111111111111111111111111111112',
          outputMint: outputMint,
          amount: Math.floor(amountInSOL * LAMPORTS_PER_SOL),
          slippageBps: slippageBps * 100,
          onlyDirectRoutes: false,
          asLegacyTransaction: false
        }
      });

      if (!quoteResponse.data) {
        throw new Error('No route found');
      }

      const swapResponse = await axios.post(`${JUPITER_API}/swap`, {
        quoteResponse: quoteResponse.data,
        userPublicKey: publicKey.toString(),
        wrapAndUnwrapSol: true,
        computeUnitPriceMicroLamports: priorityFeeLamports * 1000,
        prioritizationFeeLamports: priorityFeeLamports * LAMPORTS_PER_SOL
      });

      const { swapTransaction } = swapResponse.data;
      
      const swapTransactionBuf = Buffer.from(swapTransaction, 'base64');
      const transaction = VersionedTransaction.deserialize(swapTransactionBuf);
      
      const signedTransaction = await provider.signTransaction(transaction);
      
      const rawTransaction = signedTransaction.serialize();
      const txSignature = await connection.sendRawTransaction(rawTransaction, {
        skipPreflight: false,
        maxRetries: 3,
        preflightCommitment: 'confirmed'
      });

      const latestBlockhash = await connection.getLatestBlockhash();
      await connection.confirmTransaction({
        signature: txSignature,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
      }, 'confirmed');

      return { success: true, txSignature };
    } catch (error: any) {
      console.error('Jupiter swap failed:', error);
      return { 
        success: false, 
        error: error.message || 'Swap failed' 
      };
    }
  };

  const addTarget = async () => {
    if (!newTokenAddress) {
      alert('Please enter a token address');
      return;
    }

    try {
      const mintPubkey = new PublicKey(newTokenAddress);
      
      setLoading(true);
      const mintInfo = await connection.getAccountInfo(mintPubkey);
      
      if (!mintInfo) {
        throw new Error('Invalid token address - account not found');
      }

      const newTarget: SniperTarget = {
        id: Date.now().toString(),
        tokenAddress: newTokenAddress,
        status: 'waiting',
        hasLiquidity: false,
        liquiditySOL: 0,
        liquidityToken: 0,
        pricePerToken: 0,
        buyAmount: settings.defaultBuyAmount,
        maxSlippage: settings.defaultSlippage,
        priorityFee: settings.defaultPriorityFee,
        addedAt: new Date(),
        retryCount: 0,
        maxRetries: settings.maxRetries,
        liquidityCheckCount: 0
      };

      try {
        const metadataResponse = await axios.get(
          `https://api.helius.xyz/v0/token-metadata?api-key=37327d2d-9742-4fc4-b0d0-57d489b78697`,
          { params: { mint: newTokenAddress } }
        );
        
        if (metadataResponse.data) {
          newTarget.name = metadataResponse.data.name;
          newTarget.symbol = metadataResponse.data.symbol;
          newTarget.decimals = metadataResponse.data.decimals;
        }
      } catch (err) {
        console.log('Could not fetch token metadata');
      }

      setTargets(prev => [...prev, newTarget]);
      setNewTokenAddress('');
      
      if (isMonitoring && ws.current && ws.current.readyState === WebSocket.OPEN) {
        subscribeToToken(newTokenAddress);
      }
      
      setStats(prev => ({
        ...prev,
        totalMonitored: prev.totalMonitored + 1
      }));
    } catch (error: any) {
      alert(`Invalid token address: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeTarget = (targetId: string) => {
    setTargets(prev => prev.filter(t => t.id !== targetId));
  };

  const updateTarget = (targetId: string, updates: Partial<SniperTarget>) => {
    setTargets(prev => prev.map(t => 
      t.id === targetId ? { ...t, ...updates } : t
    ));
  };

  const getStatusIcon = (status: SniperTarget['status']) => {
    switch (status) {
      case 'waiting': return <Eye className="w-4 h-4 text-gray-400" />;
      case 'monitoring': return <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />;
      case 'buying': return <Zap className="w-4 h-4 text-blue-400 animate-pulse" />;
      case 'bought': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed': return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'stopped': return <StopCircle className="w-4 h-4 text-gray-400" />;
      default: return null;
    }
  };

  const getStatusColor = (status: SniperTarget['status']) => {
    switch (status) {
      case 'waiting': return 'text-gray-400';
      case 'monitoring': return 'text-yellow-400';
      case 'buying': return 'text-blue-400';
      case 'bought': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'stopped': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 lg:p-8 border border-gray-700">
      {/* Mobile Header */}
      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center justify-between md:justify-start md:space-x-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg">
              <Target className="w-5 h-5 md:w-6 md:h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">Token Sniper</h2>
              <p className="text-xs md:text-sm text-gray-400">Auto-buy when liquidity is added</p>
            </div>
          </div>
          
          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Desktop Controls */}
        <div className="hidden md:flex items-center space-x-4">
          {/* Stats */}
          <div className="hidden lg:flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">Monitored:</span>
              <span className="font-mono">{stats.totalMonitored}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">Success:</span>
              <span className="font-mono text-green-400">{stats.successfulSnipes}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">Failed:</span>
              <span className="font-mono text-red-400">{stats.failedSnipes}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">Spent:</span>
              <span className="font-mono text-blue-400">{stats.totalSpent.toFixed(2)} SOL</span>
            </div>
          </div>

          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>

          <button
            onClick={() => setIsMonitoring(!isMonitoring)}
            disabled={targets.length === 0 || !isConnected}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
              isMonitoring 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isMonitoring ? (
              <>
                <StopCircle className="w-5 h-5" />
                <span>Stop</span>
              </>
            ) : (
              <>
                <PlayCircle className="w-5 h-5" />
                <span>Start</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden mb-4 p-4 bg-gray-900/50 rounded-xl border border-gray-700 space-y-4">
          {/* Mobile Stats */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
              <span className="text-gray-500">Monitored:</span>
              <span className="font-mono">{stats.totalMonitored}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
              <span className="text-gray-500">Success:</span>
              <span className="font-mono text-green-400">{stats.successfulSnipes}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
              <span className="text-gray-500">Failed:</span>
              <span className="font-mono text-red-400">{stats.failedSnipes}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
              <span className="text-gray-500">Spent:</span>
              <span className="font-mono text-blue-400">{stats.totalSpent.toFixed(2)}</span>
            </div>
          </div>

          {/* Mobile Controls */}
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setShowSettings(!showSettings);
                setMobileMenuOpen(false);
              }}
              className="flex-1 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </button>

            <button
              onClick={() => {
                setIsMonitoring(!isMonitoring);
                setMobileMenuOpen(false);
              }}
              disabled={targets.length === 0 || !isConnected}
              className={`flex-1 p-2 rounded-lg font-medium transition-all flex items-center justify-center space-x-2 ${
                isMonitoring 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isMonitoring ? (
                <>
                  <StopCircle className="w-5 h-5" />
                  <span>Stop</span>
                </>
              ) : (
                <>
                  <PlayCircle className="w-5 h-5" />
                  <span>Start</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Settings Panel - Made Responsive */}
      {showSettings && (
        <div className="mb-6 p-4 md:p-6 bg-gray-900/50 rounded-xl border border-gray-700">
          <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <Shield className="w-5 h-5 text-purple-400" />
            <span>Sniper Settings</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Auto-Buy</span>
              <input
                type="checkbox"
                checked={settings.autoSnipe}
                onChange={(e) => setSettings(prev => ({ ...prev, autoSnipe: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
            </label>

            <label className="flex flex-col space-y-1">
              <span className="text-sm text-gray-400">Check Interval (sec)</span>
              <input
                type="number"
                value={settings.checkInterval}
                onChange={(e) => setSettings(prev => ({ ...prev, checkInterval: Number(e.target.value) }))}
                min={1}
                max={60}
                className="px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700 text-sm"
              />
            </label>

            <label className="flex flex-col space-y-1">
              <span className="text-sm text-gray-400">Min Liquidity (SOL)</span>
              <input
                type="number"
                value={settings.minLiquiditySOL}
                onChange={(e) => setSettings(prev => ({ ...prev, minLiquiditySOL: Number(e.target.value) }))}
                min={0}
                step={1}
                className="px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700 text-sm"
              />
            </label>

            <label className="flex flex-col space-y-1">
              <span className="text-sm text-gray-400">Max Liquidity (SOL)</span>
              <input
                type="number"
                value={settings.maxLiquiditySOL}
                onChange={(e) => setSettings(prev => ({ ...prev, maxLiquiditySOL: Number(e.target.value) }))}
                min={0}
                step={10}
                className="px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700 text-sm"
              />
            </label>

            <label className="flex flex-col space-y-1">
              <span className="text-sm text-gray-400">Buy Amount (SOL)</span>
              <input
                type="number"
                value={settings.defaultBuyAmount}
                onChange={(e) => setSettings(prev => ({ ...prev, defaultBuyAmount: Number(e.target.value) }))}
                min={0.01}
                step={0.1}
                className="px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700 text-sm"
              />
            </label>

            <label className="flex flex-col space-y-1">
              <span className="text-sm text-gray-400">Slippage (%)</span>
              <input
                type="number"
                value={settings.defaultSlippage}
                onChange={(e) => setSettings(prev => ({ ...prev, defaultSlippage: Number(e.target.value) }))}
                min={0.1}
                max={100}
                step={0.5}
                className="px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700 text-sm"
              />
            </label>
          </div>
        </div>
      )}

      {/* Add Target Section - Made Responsive */}
      <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <input
            type="text"
            value={newTokenAddress}
            onChange={(e) => setNewTokenAddress(e.target.value)}
            placeholder="Enter token contract address..."
            className="flex-1 px-4 py-2.5 bg-gray-800 rounded-lg border border-gray-700 focus:border-purple-500 transition-colors text-sm"
            disabled={loading}
          />
          <button
            onClick={addTarget}
            disabled={!newTokenAddress || !isConnected || loading}
            className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Plus className="w-5 h-5" />
            )}
            <span>Add Target</span>
          </button>
        </div>
      </div>

      {/* WebSocket Status */}
      {isMonitoring && (
        <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-yellow-400 animate-pulse" />
            <span className="text-xs sm:text-sm text-yellow-400">
              Monitoring {targets.filter(t => t.status === 'monitoring').length} targets...
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${ws.current?.readyState === WebSocket.OPEN ? 'bg-green-400' : 'bg-red-400'} animate-pulse`} />
            <span className="text-xs text-gray-500">
              {ws.current?.readyState === WebSocket.OPEN ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      )}

      {/* Targets List - Made Mobile Responsive */}
      <div className="space-y-3">
        {targets.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tokens being monitored</p>
            <p className="text-sm mt-1">Add token addresses to start sniping</p>
          </div>
        ) : (
          targets.map(target => (
            <div
              key={target.id}
              className="p-4 bg-gray-900/50 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors"
            >
              {/* Mobile View - Collapsible */}
              <div className="md:hidden">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedTarget(expandedTarget === target.id ? null : target.id)}
                >
                  <div className="flex items-center space-x-2 flex-1">
                    {getStatusIcon(target.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        {target.symbol && (
                          <span className="font-semibold text-sm">{target.symbol}</span>
                        )}
                        <span className="font-mono text-xs text-gray-400 truncate">
                          {target.tokenAddress.slice(0, 6)}...{target.tokenAddress.slice(-4)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 mt-1 text-xs">
                        <span className={getStatusColor(target.status)}>
                          {target.status.toUpperCase()}
                        </span>
                        {target.hasLiquidity && (
                          <span className="text-green-400">
                            {target.liquiditySOL.toFixed(2)} SOL
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {target.status === 'bought' && (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTarget(target.id);
                      }}
                      disabled={target.status === 'buying'}
                      className="text-red-400 hover:text-red-300 disabled:opacity-50 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedTarget === target.id && (
                  <div className="mt-3 pt-3 border-t border-gray-700 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Buy Amount:</span>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={target.buyAmount}
                          onChange={(e) => updateTarget(target.id, { buyAmount: Number(e.target.value) })}
                          min={0.01}
                          step={0.1}
                          disabled={target.status === 'bought' || target.status === 'buying'}
                          className="w-20 px-2 py-1 bg-gray-800 rounded border border-gray-700 text-xs"
                        />
                        <span className="text-xs text-gray-500">SOL</span>
                      </div>
                    </div>
                    
                    {target.liquidityCheckCount > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Checks:</span>
                        <span>{target.liquidityCheckCount}</span>
                      </div>
                    )}
                    
                    {target.lastChecked && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Last Check:</span>
                        <span>{new Date(target.lastChecked).toLocaleTimeString()}</span>
                      </div>
                    )}

                    {target.txSignature && (
                      <a
                        href={`https://solscan.io/tx/${target.txSignature}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center space-x-2 w-full py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View Transaction</span>
                      </a>
                    )}

                    {target.hasLiquidity && target.status !== 'bought' && (
                      <button
                        onClick={() => executeBuy(target.id)}
                        disabled={!isConnected || target.status === 'buying'}
                        className="w-full py-2 bg-green-600 hover:bg-green-700 rounded text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        Buy Now
                      </button>
                    )}

                    {target.error && (
                      <div className="text-xs text-red-400">
                        Error: {target.error}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop View - Original Layout */}
              <div className="hidden md:flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  {getStatusIcon(target.status)}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      {target.symbol && (
                        <span className="font-semibold">{target.symbol}</span>
                      )}
                      <span className="font-mono text-sm text-gray-400">
                        {target.tokenAddress.slice(0, 8)}...{target.tokenAddress.slice(-8)}
                      </span>
                      <button
                        onClick={() => navigator.clipboard.writeText(target.tokenAddress)}
                        className="text-gray-500 hover:text-gray-400"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                      <span className={getStatusColor(target.status)}>
                        {target.status.toUpperCase()}
                      </span>
                      {target.hasLiquidity && (
                        <>
                          <span className="flex items-center space-x-1 text-green-400">
                            <Droplets className="w-3 h-3" />
                            <span>{target.liquiditySOL.toFixed(2)} SOL</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <DollarSign className="w-3 h-3" />
                            <span>{target.pricePerToken.toFixed(8)} SOL</span>
                          </span>
                        </>
                      )}
                      {target.liquidityCheckCount > 0 && (
                        <span>Checks: {target.liquidityCheckCount}</span>
                      )}
                      {target.retryCount > 0 && (
                        <span className="text-yellow-400">
                          Retries: {target.retryCount}/{target.maxRetries}
                        </span>
                      )}
                      {target.lastChecked && (
                        <span>
                          Last: {new Date(target.lastChecked).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={target.buyAmount}
                      onChange={(e) => updateTarget(target.id, { buyAmount: Number(e.target.value) })}
                      min={0.01}
                      step={0.1}
                      disabled={target.status === 'bought' || target.status === 'buying'}
                      className="w-20 px-2 py-1 bg-gray-800 rounded border border-gray-700 text-sm"
                    />
                    <span className="text-xs text-gray-500">SOL</span>
                  </div>

                  {target.txSignature && (
                    <a
                      href={`https://solscan.io/tx/${target.txSignature}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}

                  {target.hasLiquidity && target.status !== 'bought' && (
                    <button
                      onClick={() => executeBuy(target.id)}
                      disabled={!isConnected || target.status === 'buying'}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      Buy Now
                    </button>
                  )}

                  <button
                    onClick={() => removeTarget(target.id)}
                    disabled={target.status === 'buying'}
                    className="text-red-400 hover:text-red-300 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Desktop Error Message */}
              {target.error && (
                <div className="hidden md:block mt-2 text-xs text-red-400">
                  Error: {target.error}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Audio Element for Alerts */}
      <audio
        ref={audioRef}
        src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIHWi78OScTgwOUafk4bVpGAU7k9j1y3UtBiR3zvXjfVEQFGHI8OScTgwOUafk4bVpGAU7k9j1y3UtBiR3zvXjfVEREm7Z"
      />

      {/* Wallet Connection Warning */}
      {!isConnected && (
        <div className="mt-4 text-center text-xs sm:text-sm text-yellow-400 flex items-center justify-center space-x-2">
          <AlertCircle className="w-4 h-4" />
          <span>Please connect your wallet to use the sniper</span>
        </div>
      )}
    </div>
  );
};

export default ProductionTokenSniper;